/**
 * View-Modell der Kartenansicht des Prüfberichts.
 *
 * Die Kartenansicht zeigt denselben Baum wie die Spaltenansicht, nur anders gruppiert:
 * Datei-Header → (optional) Positions-Header → eine Karte je Prüfregel → Ergebnis-Blöcke im Body.
 * Sie leitet sich aus **denselben** Primitiven ab (Ebenen-Reihenfolge, sichtbare Spalten, Labels,
 * Blattliste, Detail-Einträge) und ist damit genauso schema-getrieben wie die Spaltenansicht.
 *
 * Zwei Entwurfsentscheidungen, die den Code klein halten:
 *
 * 1. **Die Ebenen-Rollen ergeben sich aus der Reihenfolge**, nicht aus dem Namen. Erste Ebene =
 *    Gruppen-Header, mittlere = Abschnitts-Header, letzte = Karte. Damit funktioniert dieselbe
 *    Funktion für Datei→Regel, Datei→Position→Regel und die rules-first-Perspektive
 *    (Regel→Datei), ohne Sonderfälle.
 * 2. **IDS und KI/Hybrid sind ein Code-Pfad.** Ob ein Ergebnis-Block eine Facetten-Zeile (IDS) oder
 *    ein Prüfschritt-Kasten (KI) ist, entscheiden die Daten des Details – nicht die Prüfungsart.
 *    Ein Hybrid-Bericht, der irgendwann Facetten liefert, rendert dadurch automatisch korrekt.
 */

import {
  countStatus,
  formatFailureReason,
  NormalizedReport,
  ReportChip,
  ReportDetail,
  ReportSourceFile,
} from './normalized-report.model';
import {ReportDisplayConfig, ResolvedDetailEntry} from './report-display-config';
import {
  columnLabel,
  identifyingColumnKey,
  isLevelColumnVisible,
  isLevelStatusVisible,
  levelLabel,
  orderedColumnKeys,
  resolveIdsLevelKeys,
  resolveRenderedColumnKeys,
} from './report-levels';
import {
  aggregateColumnItems,
  buildReportLeaves,
  columnItemValue,
  leafValue,
  ReportColumnItem,
  ReportLeaf,
} from './report-leaves';
import {
  DetailColumn,
  formatDetailRating,
  isFacetDetail,
  resolveConstantFieldKeys,
  resolveDetailColumns,
  shouldShowRawOutput,
  splitDetailEntries,
} from './report-detail-blocks';
import {filterReportLeaves, ReportFilterState, ReportStatus} from './report-filter';

/** Badge links im Karten-Header: Prüfregelkürzel oder Prozentwert. */
export interface CardBadge {
  text: string;
  type: 'info' | 'success' | 'warning' | 'error' | 'unknown';
  /** Prozent-Badges haben laut Entwurf eine feste Breite, damit die Zeilen fluchten. */
  fixedWidth: boolean;
}

/** Ein zusätzlicher konfigurierter Wert (Label + Text), z. B. Dateiformat oder Ergebnis. */
export interface CardMetaValue {
  key: string;
  label: string;
  value: string;
}

/** Zähl-Badge eines Gruppen-/Abschnitts-Headers („5 erfüllt"). */
export interface CardStatusCount {
  count: number;
  label: string;
  type: 'success' | 'warning' | 'error' | 'unknown';
}

/**
 * Gemeinsame Kopfzeile von Gruppe, Abschnitt und Karte. Alle drei zeigen dieselben Bausteine –
 * welcher davon belegt ist, hängt an den konfigurierten Spalten der jeweiligen Ebene.
 */
export interface CardNode {
  key: string;
  levelKey: string;
  title: string;
  titleLabel: string;
  badge?: CardBadge;
  description?: string;
  secondaryText?: string;
  meta: CardMetaValue[];
  chips: ReportChip[];
  status: ReportStatus;
  percent: number | null;
  /** Ob der Status dargestellt wird (Statusstreifen der Karte bzw. Icon im Header). */
  showStatus: boolean;
}

/** Ein Ergebnis-Block im Karten-Body. */
export type CardBodyBlock =
  | {
  kind: 'step';
  key: string;
  status?: ReportStatus;
  heading?: string;
  entries: ResolvedDetailEntry[];
  ratingText?: string;
  /** Herangezogene Quelldateien dieses Prüfschritts (nur Prüfmodus COMBINED); Ziel der Quellen-Badges. */
  sourceFiles?: ReportSourceFile[];
}
  | {
  kind: 'facet';
  key: string;
  status?: ReportStatus;
  percentText?: string;
  badgeType: 'success' | 'warning' | 'error' | 'unknown';
  title: string;
  chips: ReportChip[];
  secondaryText?: string;
  /**
   * Warum die Anforderung gerissen ist, je Grund eine Zeile (IDS). Leer bei erfüllten
   * Anforderungen und bei Berichten von vor dieser Erweiterung.
   */
  reasonTexts?: string[];
  hasTask: boolean;
}
  | { kind: 'params'; key: string; label: string; entries: ResolvedDetailEntry[] }
  | { kind: 'raw'; key: string; json: unknown };

export interface CardItem {
  node: CardNode;
  body: CardBodyBlock[];
}

export interface CardSection {
  /** Fehlt, wenn es keine Zwischenebene gibt – dann folgen die Karten direkt auf den Datei-Header. */
  node?: CardNode;
  counts: CardStatusCount[];
  items: CardItem[];
}

export interface CardGroup {
  /** Fehlt, wenn nur eine Ebene konfiguriert ist – dann gibt es keinen Gruppen-Header. */
  node?: CardNode;
  counts: CardStatusCount[];
  sections: CardSection[];
}

export interface CardModel {
  groups: CardGroup[];
  /** Alle Karten-Keys flach – Basis für „alles auf-/zuklappen" ohne Baumdurchlauf. */
  itemKeys: string[];
}

export const EMPTY_CARD_MODEL: CardModel = {groups: [], itemKeys: []};

/** Spaltenschlüssel, die als Prozent-/Ergebniswert gelten. */
const RESULT_PERCENT_KEYS = ['rating', 'result'];

function statusBadgeType(status: ReportStatus): 'success' | 'warning' | 'error' | 'unknown' {
  return status;
}

/**
 * Ebenen der Kartenansicht (ohne `detail`), in Anzeigereihenfolge.
 *
 * Anders als die Spaltenansicht behält die Kartenansicht die Dateiebene auch bei nur einer geprüften
 * Datei: dort wäre eine Auswahlspalte mit einem Eintrag nutzlos, hier trägt der Datei-Header den
 * Dateinamen und die Zähl-Badges – so zeigt der Entwurf es auch für IDS mit einer Datei.
 */
export function resolveCardLevelKeys(
  report: NormalizedReport | null | undefined,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
): string[] {
  if ((checkType ?? '').toUpperCase() === 'IDS') {
    const idsLevels = resolveIdsLevelKeys(report, config)
      .filter(key => key !== 'requirements')
      .map(key => key === 'specifications' ? 'rules' : key);
    const levels = idsLevels.includes('files') ? idsLevels : ['files', ...idsLevels];
    return levels.includes('rules') ? levels : [...levels, 'rules'];
  }
  const levels = resolveRenderedColumnKeys(report, config) as string[];
  return levels.length > 0 ? levels : ['files', 'rules'];
}

/** Zähl-Badges („5 erfüllt / 2 teilweise erfüllt / 3 nicht erfüllt"); nur Stati mit Anzahl > 0. */
function buildStatusCounts(items: ReadonlyArray<{ status: ReportStatus }>): CardStatusCount[] {
  const counts = countStatus(items);
  return [
    {count: counts.success, label: `${counts.success} erfüllt`, type: 'success' as const},
    {count: counts.warning, label: `${counts.warning} teilweise erfüllt`, type: 'warning' as const},
    {count: counts.error, label: `${counts.error} nicht erfüllt`, type: 'error' as const},
    {count: counts.unknown, label: `${counts.unknown} unbekannt`, type: 'unknown' as const},
  ].filter(entry => entry.count > 0);
}

/**
 * Baut die Kopfzeile eines Knotens aus den konfigurierten Spalten seiner Ebene.
 *
 * Belegungsregeln:
 * - identifizierende Spalte → `title`
 * - `shortCode` → Kürzel-Badge; fehlt das Kürzel, tritt der Prozentwert an seine Stelle (so bekommt
 *   IDS automatisch den Prozent-Badge des Entwurfs, ohne Sonderfall für die Prüfungsart)
 * - `description` → grauer Beschreibungstext (Default sichtbar, damit auch Altberichte dem Entwurf
 *   entsprechen; explizites Ausblenden wird respektiert)
 * - `elements` → Sekundärtext „N Bauteile"
 * - `applicability` → Chips
 * - alle übrigen sichtbaren Spalten → `meta`
 */
function buildNode(
  key: string,
  levelKey: string,
  item: ReportColumnItem,
  leaves: ReadonlyArray<ReportLeaf>,
  config: ReportDisplayConfig | null | undefined,
): CardNode {
  const idKey = identifyingColumnKey(levelKey);
  const columnKeys = orderedColumnKeys(config, levelKey);
  const ratingVisible = columnKeys.some(columnKey => RESULT_PERCENT_KEYS.includes(columnKey));
  const shortCodeVisible = columnKeys.includes('shortCode');

  const badge: CardBadge | undefined = shortCodeVisible && item.shortCode
    ? {text: item.shortCode, type: 'info', fixedWidth: false}
    : ratingVisible
      // Prozent-Badges laut Entwurf ohne Leerzeichen ("20%", nicht "20 %") – anders als die
      // "Ergebnis"-Spalte der Spaltenansicht (`columnItemValue`), die bewusst "20 %" zeigt. Beide
      // Konventionen bestehen im Entwurf parallel: die IDS-Spezifikationsspalte der Spaltenansicht
      // formatiert ebenfalls ohne Leerzeichen (`{{ spec.percent ?? 0 }}%`).
      // Kein bewertetes Ergebnis → „–" statt einer Prozentzahl. Ohne diesen Fall stand hier
      // „null%“ bzw. eine erfundene 0/50 %.
      ? {
        text: item.percent == null ? '–' : `${item.percent}%`,
        type: statusBadgeType(item.status),
        fixedWidth: true,
      }
      : undefined;

  const descriptionVisible = isLevelColumnVisible(config, levelKey, 'description', true);
  const elementsVisible = isLevelColumnVisible(config, levelKey, 'elements', true);
  const applicabilityVisible = isLevelColumnVisible(config, levelKey, 'applicability', true);

  const rule = leaves[0]?.rule;
  const elementCount = levelKey === 'rules' ? rule?.elementCount : undefined;
  const applicabilities = levelKey === 'rules' ? rule?.applicabilities : undefined;

  // Spalten, die schon als Badge oder Titel dargestellt werden, nicht doppelt als Meta-Wert zeigen.
  const consumed = new Set<string>([idKey]);
  if (badge?.type === 'info') consumed.add('shortCode');
  if (badge?.fixedWidth) for (const ratingKey of RESULT_PERCENT_KEYS) consumed.add(ratingKey);
  consumed.add('description');
  consumed.add('elements');
  consumed.add('applicability');

  const meta: CardMetaValue[] = columnKeys
    .filter(columnKey => !consumed.has(columnKey))
    .map(columnKey => ({
      key: columnKey,
      label: columnLabel(config, levelKey, columnKey),
      value: columnItemValue(item, levelKey, columnKey),
    }))
    .filter(entry => entry.value !== '');

  return {
    key,
    levelKey,
    title: item.name,
    titleLabel: levelLabel(config, levelKey),
    badge,
    description: descriptionVisible ? (item.description || undefined) : undefined,
    secondaryText: elementsVisible && elementCount != null ? `${elementCount} Bauteile` : undefined,
    meta,
    chips: applicabilityVisible ? (applicabilities ?? []) : [],
    status: item.status,
    percent: item.percent,
    showStatus: isLevelStatusVisible(config, levelKey),
  };
}

/**
 * Ergebnis-Blöcke einer Karte. Facetten-Zeilen (IDS) und Prüfschritt-Kästen (KI/Hybrid) werden
 * datengetrieben unterschieden; regelweit konstante Felder erscheinen einmal als „Regelparameter"
 * statt in jedem Prüfschritt wiederholt.
 */
function buildBody(
  itemKey: string,
  details: ReadonlyArray<ReportDetail>,
  detailColumns: ReadonlyArray<DetailColumn>,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
): CardBodyBlock[] {
  const blocks: CardBodyBlock[] = [];
  const constantKeys = resolveConstantFieldKeys(details);
  const showStatus = isLevelStatusVisible(config, 'detail');
  const showRaw = shouldShowRawOutput(checkType);
  const constantEntries: ResolvedDetailEntry[] = details.length > 0
    ? splitDetailEntries(details[0], detailColumns, constantKeys).constant
    : [];

  details.forEach((detail, index) => {
    const key = `${itemKey}~${index}`;
    const isFacet = isFacetDetail(detail);

    if (isFacet) {
      const facetTitle = [detail.facetLabel, detail.name].filter(Boolean).join(': ')
        || detail.text || '–';
      blocks.push({
        kind: 'facet',
        key,
        status: showStatus ? detail.type : undefined,
        percentText: detail.percent != null ? `${detail.percent}%` : undefined,
        badgeType: statusBadgeType(detail.type),
        title: facetTitle,
        chips: detail.valueChips ?? [],
        secondaryText: detail.count > 0 ? `${detail.count} Bauteile` : undefined,
        reasonTexts: detail.failureReasons?.map(formatFailureReason),
        hasTask: false,
      });
      // Die Facette IST die strukturierte Darstellung (IDS) – ein zusätzlicher Roh-JSON-Block
      // wäre reine Redundanz, unabhängig von `shouldShowRawOutput`/checkType. Der IDS-Adapter
      // füllt `rawOutput` auf jedem Requirement (check-report-adapter.ts), die Spaltenansicht
      // zeigt es dort aber nie, weil ihr IDS-Zweig komplett getrennt ist und `rawOutput` nicht
      // referenziert – hier also bewusst dieselbe Auslassung nachbilden, aber datengetrieben statt
      // über eine IDS-Sonderregel.
    } else {
      const {perStep} = splitDetailEntries(detail, detailColumns, constantKeys);
      blocks.push({
        kind: 'step',
        key,
        status: showStatus ? detail.type : undefined,
        heading: detail.heading || undefined,
        entries: perStep,
        ratingText: formatDetailRating(detail) ?? undefined,
        sourceFiles: detail.sourceFiles,
      });
      if (showRaw && detail.rawOutput) {
        blocks.push({kind: 'raw', key: `${key}~raw`, json: detail.rawOutput});
      }
    }
  });

  if (constantEntries.length > 0) {
    blocks.push({
      kind: 'params',
      key: `${itemKey}~params`,
      label: 'Regelparameter',
      entries: constantEntries,
    });
  }

  return blocks;
}

/** Gruppiert Blätter nach dem identifizierenden Wert einer Ebene, in Auftrittsreihenfolge. */
function groupLeavesBy(
  leaves: ReadonlyArray<ReportLeaf>,
  levelKey: string,
): Array<{ name: string; leaves: ReportLeaf[] }> {
  const byName = new Map<string, ReportLeaf[]>();
  for (const leaf of leaves) {
    const name = leafValue(leaf, levelKey);
    const bucket = byName.get(name);
    if (bucket) {
      bucket.push(leaf);
    } else {
      byName.set(name, [leaf]);
    }
  }
  return [...byName.entries()].map(([name, bucketLeaves]) => ({name, leaves: bucketLeaves}));
}

/**
 * Wie {@link groupLeavesBy}, aber in derselben Reihenfolge wie `aggregateColumnItems`
 * (siehe report-leaves.ts): Dateien nach höchstem Ergebnis, Prüfregeln alphabetisch nach Kürzel.
 * Damit sortiert die Kartenansicht identisch zur Spaltenansicht. Andere Ebenen (z. B. Positionen)
 * behalten die Auftrittsreihenfolge, weil `aggregateColumnItems` sie unsortiert lässt.
 */
function orderedGroupLeavesBy(
  leaves: ReadonlyArray<ReportLeaf>,
  levelKey: string,
): Array<{ name: string; leaves: ReportLeaf[] }> {
  const buckets = groupLeavesBy(leaves, levelKey);
  const byName = new Map(buckets.map(bucket => [bucket.name, bucket]));
  const order = aggregateColumnItems(leaves, levelKey).map(item => item.name);
  return order
    .map(name => byName.get(name))
    .filter((bucket): bucket is { name: string; leaves: ReportLeaf[] } => !!bucket);
}

function nodeFor(
  key: string,
  levelKey: string,
  leaves: ReadonlyArray<ReportLeaf>,
  config: ReportDisplayConfig | null | undefined,
): CardNode {
  const item = aggregateColumnItems(leaves, levelKey)[0]
    ?? {name: leafValue(leaves[0], levelKey), status: 'unknown' as ReportStatus, percent: null};
  return buildNode(key, levelKey, item, leaves, config);
}

/**
 * Baut das vollständige Karten-Modell.
 *
 * **Nicht aus einem Template aufrufen** – die Funktion läuft über den ganzen Bericht. Die
 * aufrufende Komponente memoisiert das Ergebnis über (report, config, checkType, filter).
 */
export function buildCardModel(
  report: NormalizedReport | null | undefined,
  config: ReportDisplayConfig | null | undefined,
  checkType: string | null | undefined,
  filter?: ReportFilterState | null,
): CardModel {
  if (!report) return EMPTY_CARD_MODEL;

  const levelKeys = resolveCardLevelKeys(report, config, checkType);
  const allLeaves = buildReportLeaves(report, levelKeys);
  const leaves = filterReportLeaves(allLeaves, filter);
  if (leaves.length === 0) return EMPTY_CARD_MODEL;

  const itemLevel = levelKeys[levelKeys.length - 1];
  const groupLevel = levelKeys.length >= 2 ? levelKeys[0] : undefined;
  const sectionLevel = levelKeys.length >= 3 ? levelKeys[1] : undefined;
  const detailColumns = resolveDetailColumns(config);

  const buildItems = (
    scoped: ReadonlyArray<ReportLeaf>, keyPrefix: string,
  ): CardItem[] =>
    orderedGroupLeavesBy(scoped, itemLevel).map((bucket, itemIdx) => {
      const itemKey = `${keyPrefix}#${itemIdx}|${bucket.name}`;
      const details = bucket.leaves.flatMap(leaf => leaf.rule.details ?? []);
      return {
        node: nodeFor(itemKey, itemLevel, bucket.leaves, config),
        body: buildBody(itemKey, details, detailColumns, config, checkType),
      };
    });

  const groupBuckets = groupLevel
    ? orderedGroupLeavesBy(leaves, groupLevel)
    : [{name: '', leaves: [...leaves]}];

  const groups: CardGroup[] = groupBuckets.map((groupBucket, groupIdx) => {
    const groupKey = `${groupIdx}|${groupBucket.name}`;

    const sections: CardSection[] = sectionLevel
      ? orderedGroupLeavesBy(groupBucket.leaves, sectionLevel).map((sectionBucket, sectionIdx) => {
        const sectionKey = `${groupKey}#${sectionIdx}|${sectionBucket.name}`;
        const items = buildItems(sectionBucket.leaves, sectionKey);
        return {
          node: nodeFor(sectionKey, sectionLevel, sectionBucket.leaves, config),
          counts: buildStatusCounts(items.map(item => item.node)),
          items,
        };
      })
      : [(() => {
        const items = buildItems(groupBucket.leaves, `${groupKey}#0|`);
        return {counts: buildStatusCounts(items.map(item => item.node)), items};
      })()];

    const allItems = sections.flatMap(section => section.items);
    return {
      node: groupLevel ? nodeFor(groupKey, groupLevel, groupBucket.leaves, config) : undefined,
      counts: buildStatusCounts(allItems.map(item => item.node)),
      sections,
    };
  });

  const itemKeys = groups
    .flatMap(group => group.sections)
    .flatMap(section => section.items)
    .map(item => item.node.key);

  return {groups, itemKeys};
}
